/**
 * Native feature bridge — runtime detection of Capacitor shell vs browser.
 *
 * The same React code runs in:
 *   1. Desktop browser (full web APIs)
 *   2. Mobile browser / PWA (responsive web)
 *   3. Capacitor native shell (gets access to Camera, Push, Biometric, etc.)
 *
 * This module picks the best available API at runtime. UI code shouldn't
 * care where it runs — it just calls getCameraPhoto() and the right
 * implementation fires.
 */

interface CapacitorRuntime {
  isNativePlatform: () => boolean;
  getPlatform: () => 'web' | 'ios' | 'android';
}

declare global {
  interface Window {
    Capacitor?: CapacitorRuntime;
  }
}

export function isNativeShell(): boolean {
  return !!window.Capacitor?.isNativePlatform?.();
}

export function platform(): 'web' | 'ios' | 'android' {
  return (window.Capacitor?.getPlatform?.() as 'web' | 'ios' | 'android') ?? 'web';
}

// ─── Camera (bill photo capture) ─────────────────────────────────────────────
export async function getCameraPhoto(): Promise<Blob | null> {
  if (isNativeShell()) {
    try {
      const { Camera, CameraResultType, CameraSource } = await import(
        '@capacitor/camera'
      );
      const photo = await Camera.getPhoto({
        quality: 70,
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt, // user picks camera or gallery
        width: 1920,
      });
      if (photo.webPath) {
        const resp = await fetch(photo.webPath);
        return await resp.blob();
      }
      return null;
    } catch (err) {
      console.error('[Prism] Native camera failed:', err);
      return null;
    }
  }

  // Web fallback: open file picker with capture preference
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.setAttribute('capture', 'environment');
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

// ─── Secure storage (for API tokens, session state) ──────────────────────────
export async function secureSet(key: string, value: string): Promise<void> {
  if (isNativeShell()) {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key, value });
    return;
  }
  localStorage.setItem(`prism_${key}`, value);
}

export async function secureGet(key: string): Promise<string | null> {
  if (isNativeShell()) {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key });
    return value;
  }
  return localStorage.getItem(`prism_${key}`);
}

export async function secureRemove(key: string): Promise<void> {
  if (isNativeShell()) {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key });
    return;
  }
  localStorage.removeItem(`prism_${key}`);
}

// ─── Online / offline detection ──────────────────────────────────────────────
export async function isOnline(): Promise<boolean> {
  if (isNativeShell()) {
    const { Network } = await import('@capacitor/network');
    const status = await Network.getStatus();
    return status.connected;
  }
  return navigator.onLine;
}

export function onNetworkChange(cb: (online: boolean) => void): () => void {
  if (isNativeShell()) {
    let off: (() => void) | null = null;
    import('@capacitor/network').then(({ Network }) => {
      Network.addListener('networkStatusChange', (s) => cb(s.connected)).then(
        (handle) => {
          off = () => handle.remove();
        },
      );
    });
    return () => off?.();
  }
  const online = () => cb(true);
  const offline = () => cb(false);
  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  return () => {
    window.removeEventListener('online', online);
    window.removeEventListener('offline', offline);
  };
}

// ─── Biometric auth (Face ID / Touch ID / Android biometric) ────────────────

export type BiometricKind = 'faceId' | 'touchId' | 'fingerprintAuthentication' | 'none';

export interface BiometricStatus {
  available: boolean;
  kind: BiometricKind;
  reason?: string;
}

/** Check what biometric the device supports. On web returns { available: false }. */
export async function biometricStatus(): Promise<BiometricStatus> {
  if (!isNativeShell()) return { available: false, kind: 'none', reason: 'not native' };
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const info = await BiometricAuth.checkBiometry();
    if (!info.isAvailable) {
      return {
        available: false,
        kind: 'none',
        reason: info.reason ?? info.code ?? 'unavailable',
      };
    }
    let kind: BiometricKind = 'none';
    // BiometryType numeric codes: 0=none, 1=touchId, 2=faceId, 3=fingerprint
    const t = info.biometryType as number;
    if (t === 1) kind = 'touchId';
    else if (t === 2) kind = 'faceId';
    else if (t === 3) kind = 'fingerprintAuthentication';
    return { available: true, kind };
  } catch (err) {
    return {
      available: false,
      kind: 'none',
      reason: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Prompt the user for biometric verification.
 * Returns true if verified, false on user cancel / lockout / error.
 */
export async function biometricVerify(reason: string): Promise<boolean> {
  if (!isNativeShell()) return true; // web: skip (we rely on MSAL)
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true, // fall back to passcode if biometric fails 3×
      iosFallbackTitle: 'Use passcode',
      androidTitle: 'Prism — verify it\'s you',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return true;
  } catch {
    return false;
  }
}

// ─── Push notifications (native only) ────────────────────────────────────────
export async function registerPushNotifications(): Promise<string | null> {
  if (!isNativeShell()) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return null;
    await PushNotifications.register();
    return await new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => {
        resolve(token.value);
      });
    });
  } catch (err) {
    console.error('[Prism] Push registration failed:', err);
    return null;
  }
}

// ─── App lifecycle hooks ─────────────────────────────────────────────────────
export async function onAppResumed(cb: () => void): Promise<() => void> {
  if (isNativeShell()) {
    const { App } = await import('@capacitor/app');
    const handle = await App.addListener('resume', cb);
    return () => handle.remove();
  }
  // Web equivalent: page visibility
  const handler = () => {
    if (!document.hidden) cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}
